import { GenerationRequestSchema, type GenerationKind, type GenerationRequest } from '../domain/schemas';
import { LocalProceduralProvider } from './proceduralProvider';
import { ProviderError, type GenerationProvider, type GenerationResult } from './types';
import { UnconfiguredProvider } from './unconfiguredProvider';

export class ProviderRegistry {
  private readonly providers: GenerationProvider[];

  constructor(providers: GenerationProvider[]) {
    this.providers = providers;
  }

  list(): readonly GenerationProvider[] {
    return this.providers;
  }

  /** Returns the first *available* provider for a kind, else the first registered one. */
  forKind(kind: GenerationKind): GenerationProvider | undefined {
    const candidates = this.providers.filter((p) => p.capabilities.kinds.includes(kind));
    return candidates.find((p) => p.availability().state === 'available') ?? candidates[0];
  }

  async generate(request: GenerationRequest, signal?: AbortSignal): Promise<GenerationResult> {
    const parsed = GenerationRequestSchema.safeParse(request);
    if (!parsed.success) throw new ProviderError('invalid-request', 'Generation request is invalid.');
    const provider = this.providers.find((p) => p.capabilities.providerId === request.providerId);
    if (!provider) throw new ProviderError('unavailable', `Unknown provider "${request.providerId}".`);
    const availability = provider.availability();
    if (availability.state !== 'available') throw new ProviderError('unavailable', availability.reason);
    if (request.prompt.length > provider.capabilities.maxPromptLength) {
      throw new ProviderError('invalid-request', 'Prompt is too long for this provider.');
    }
    if (signal?.aborted) throw new ProviderError('cancelled', 'Generation was cancelled.');
    return provider.generate(parsed.data, signal);
  }
}

/** Phase 0 registry: one real local procedural provider plus unconfigured placeholders. */
export function createDefaultRegistry(): ProviderRegistry {
  return new ProviderRegistry([
    new LocalProceduralProvider(),
    new UnconfiguredProvider('text-to-music'),
    new UnconfiguredProvider('text-to-audio'),
    new UnconfiguredProvider('text-to-video'),
    new UnconfiguredProvider('speech'),
  ]);
}
